import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import useFetchData from '@/hooks/useFetchData';
import { useTranslate } from '@/hooks/useTranslate';
import { fetchViralTemplatePage } from '@/services/viralTemplate';
import { ViralTemplateExposeFromEnum } from '@/utils/eventlog/eventNewConfig';

import { useViralTemplateFormModal } from '../../../../hooks/useViralTemplateFormModal';
import {
  buildViralTemplateUgcOverviewQuery,
  VIRAL_LIST_CATEGORY_BY_TYPE_TAB,
  ViralTemplateTabs,
  ViralTemplateTypeTab,
} from '../../../../hooks/useViralTemplateRouteState';
import type { TplTemplateVO } from '../../../template-ui/types';
import CategorySectionShell from './CategorySectionShell';
import { shouldShowPreviewViewMore } from './showViewMore';
import { useColumns } from './useColumns';

export default function UgcPreviewSection({
  onViewMore,
}: {
  onViewMore: (subtab?: ViralTemplateTypeTab) => void;
}) {
  const router = useRouter();
  const { t } = useTranslate();
  const { openFormModal, loadingDetail } = useViralTemplateFormModal(
    ViralTemplateExposeFromEnum.TEMPLATE,
  );
  const { ref, columns } = useColumns();

  /** 仅当 URL 显式带合法 `subtab` 时同步（无 query 则用本地 activeTab，避免误用默认 trending） */
  const subtabInUrl = useMemo((): ViralTemplateTypeTab | undefined => {
    const raw = router.query.subtab;
    if (typeof raw !== 'string') return undefined;
    return ViralTemplateTabs.some((tab) => tab.key === raw)
      ? (raw as ViralTemplateTypeTab)
      : undefined;
  }, [router.query.subtab]);

  // 初始值直接从 URL 读取（组件仅在 router.isReady 后挂载，subtabInUrl 此时已可用）
  const [activeTab, setActiveTab] = useState<ViralTemplateTypeTab>(
    subtabInUrl ?? ViralTemplateTabs[0].key,
  );

  useEffect(() => {
    if (subtabInUrl !== undefined) {
      setActiveTab(subtabInUrl);
    }
  }, [subtabInUrl]);

  const handleTabChange = useCallback(
    (nextTab: ViralTemplateTypeTab) => {
      setActiveTab(nextTab);
      void router.push(
        {
          pathname: router.pathname,
          query: buildViralTemplateUgcOverviewQuery(nextTab),
        },
        undefined,
        { shallow: true },
      );
    },
    [router],
  );

  // const pageSize = columns * 2;
  const pageSize = 40;

  const fetchFn = useCallback(
    (pageNum: number, pageSizeArg: number) =>
      fetchViralTemplatePage({
        category: VIRAL_LIST_CATEGORY_BY_TYPE_TAB[activeTab],
        pageNum,
        pageSize: pageSizeArg,
      }),
    [activeTab],
  );

  const {
    data: items,
    loading,
    rawResponse,
    total,
    refresh,
  } = useFetchData<TplTemplateVO>({
    fetchFn,
    pageSize,
  });

  useEffect(() => {
    void refresh();
  }, [activeTab, pageSize, refresh]);

  const showViewMore = useMemo(() => {
    const totalCount =
      typeof rawResponse?.totalCount === 'number' ? rawResponse.totalCount : total;
    return shouldShowPreviewViewMore({
      loading,
      success: rawResponse?.code === 200 && rawResponse?.success === true,
      itemCount: items.length,
      pageSize,
      totalCount,
      hasMore: Boolean(rawResponse?.hasMore),
    });
  }, [items.length, loading, rawResponse, total, pageSize]);

  const header = useMemo(
    () => (
      <div className="flex flex-col gap-[4px] w-full">
        <span className="font-[600] text-[16px] leading-[22px] text-[#111111]">
          {t('home.socialUgcAds')}
        </span>
        <span className="text-[13px] text-[#11111199] leading-[18px]">
          {t('home.socialUgcAdsDesc')}
        </span>
      </div>
    ),
    [t],
  );

  const tabs = useMemo(
    () => (
      <div className="flex gap-[8px] w-full">
        {ViralTemplateTabs.map((tab) => (
          <button
            key={tab.key}
            className={`px-[12px] py-[4px] text-[#111111CC] bg-[#F3F3F3] rounded-[4px] text-[13px] leading-[18px] cursor-pointer border border-solid transition-colors ${
              activeTab === tab.key
                ? ' border-[#4931FA] bg-[#f4f5fe]'
                : 'hover:border-[#4931FA]'
            }`}
            onClick={() => handleTabChange(tab.key)}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>
    ),
    [activeTab, handleTabChange, t],
  );

  return (
    <>
      <CategorySectionShell
        header={header}
        tabs={tabs}
        gridAnchorRef={ref}
        items={items}
        loading={loading}
        from={ViralTemplateExposeFromEnum.TEMPLATE}
        columns={columns}
        showViewMore={showViewMore}
        onViewMore={() => onViewMore(activeTab)}
        onReplicate={(tpl) => void openFormModal(tpl)}
      />
      {loadingDetail ? (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/25">
          <div className="h-[20px] w-[20px] animate-spin rounded-full border-2 border-[#4931FA]/40 border-t-[#4931FA]" />
        </div>
      ) : null}
    </>
  );
}
